import { useEffect, useState } from "react";
import {
    fetchPrompts,
    addNewPrompt,
    updatePrompt,
    deletePrompt,
} from "../../store/smileDesign/smilePromptSlice";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { useToast } from "../../components/utilities/Toast";
import { Helmet } from "react-helmet-async";
import { FiSearch } from "react-icons/fi";
import { useDebouncedCallback } from "use-debounce";
import CreatePromptModal from "../../components/smileDesign/PromptManager/CreatePromptModal";
import PromptDisplay from "../../components/smileDesign/PromptManager/PromptDisplay";
import TablePagination from "../../components/smileDesign/TablePagination";

export default function PromptManager() {
    const dispatch = useAppDispatch();
    const { showToast } = useToast();

    const { allPrompts, promptsPagination, loading, analyzing, error } = useAppSelector(
        (s) => s.smilePrompt
    );

    const [page, setPage] = useState(1);

    const [name, setName] = useState("");
    const [heading, setHeading] = useState("");
    const [description, setDescription] = useState("");
    const [active, setActive] = useState(true);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");
    const [stlFile, setStlFile] = useState<File | null>(null);
    const [coverFile, setCoverFile] = useState<File | null>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [saving, setSaving] = useState(false);

    const [editActive, setEditActive] = useState(true);
    const [editHeading, setEditHeading] = useState("");
    const [editStlFile, setEditStlFile] = useState<File | null>(null);
    const [editCoverFile, setEditCoverFile] = useState<File | null>(null);

    const [editId, setEditId] = useState<number | null>(null);
    const [editName, setEditName] = useState("");
    const [editDescription, setEditDescription] = useState("");

    const [modal, setModal] = useState<{
        type: "delete" | null;
        id?: number;
    }>({ type: null });

    const loadPrompts = (overrides?: {
        page?: number;
        search?: string;
        statusFilter?: "all" | "active" | "inactive";
    }) => {
        const nextStatus = overrides?.statusFilter ?? statusFilter;
        dispatch(
            fetchPrompts({
                page: overrides?.page ?? page,
                search: (overrides?.search ?? search) || undefined,
                active:
                    nextStatus === "all"
                        ? undefined
                        : nextStatus === "active",
            })
        );
    };

    const debouncedSearch = useDebouncedCallback((value: string) => {
        setPage(1);
        loadPrompts({ page: 1, search: value });
    }, 400);

    useEffect(() => {
        loadPrompts();
    }, []);

    const handleSearchChange = (value: string) => {
        setSearch(value);
        debouncedSearch(value);
    };

    const handleStatusFilterChange = (next: "all" | "active" | "inactive") => {
        if (next === statusFilter) return;
        setStatusFilter(next);
        setPage(1);
        loadPrompts({ page: 1, statusFilter: next });
    };

    const autoResizeTextarea = (
        e: React.ChangeEvent<HTMLTextAreaElement>
    ) => {
        e.target.style.height = "auto";
        e.target.style.height = `${e.target.scrollHeight}px`;
    };

    const resetForm = () => {
        setName("");
        setHeading("");
        setDescription("");
        setActive(true);
        setStlFile(null);
        setCoverFile(null);
    };

    const handleCreatePrompt = async () => {
        const finalHeading = heading.trim() || name.trim();
        const finalName = name.trim() || finalHeading;
        if (!finalHeading) {
            showToast("Heading is required", "error");
            return;
        }
        if (!stlFile) {
            showToast("Original STL file is required", "error");
            return;
        }
        if (!coverFile) {
            showToast("Cover image is required", "error");
            return;
        }

        try {
            setSaving(true);
            await dispatch(
                addNewPrompt({
                    name: finalName,
                    heading: finalHeading,
                    value: description.trim(),
                    prompt_type: "base",
                    active,
                    arch: "upper",
                    stlFile,
                    imageFile: coverFile,
                })
            ).unwrap();
            resetForm();
            setShowCreateModal(false);
            showToast("Filter saved. STL stored on Box for generation.", "success");
            loadPrompts({ page: 1 });
            setPage(1);
        } catch (err) {
            showToast(typeof err === "string" ? err : "Could not create filter", "error");
        } finally {
            setSaving(false);
        }
    };

    const handleSaveEdit = async (id: number) => {
        const finalHeading = editHeading.trim() || editName.trim();
        const finalName = editName.trim() || finalHeading;
        if (!finalHeading) {
            showToast("Heading is required", "error");
            return;
        }

        try {
            setSaving(true);
            await dispatch(
                updatePrompt({
                    id,
                    name: finalName,
                    heading: finalHeading,
                    value: editDescription.trim(),
                    active: editActive,
                    prompt_type: "base",
                    arch: "upper",
                    stlFile: editStlFile,
                    imageFile: editCoverFile,
                })
            ).unwrap();
            showToast(
                editStlFile
                    ? "STL updated on Box"
                    : editCoverFile
                        ? "Cover image updated"
                        : "Filter updated",
                "success"
            );
            setEditId(null);
            setEditStlFile(null);
            setEditCoverFile(null);
            loadPrompts();
        } catch (err) {
            showToast(typeof err === "string" ? err : "Could not update filter", "error");
        } finally {
            setSaving(false);
        }
    };

    const confirmAction = () => {
        if (modal.type === "delete" && modal.id) {
            dispatch(deletePrompt(modal.id));
            showToast("Filter deleted", "success");
            loadPrompts();
        }

        setModal({ type: null });
    };

    const busy = analyzing || saving;

    return (
        <>
            <Helmet>
                <title>Prompts & Filters</title>
            </Helmet>

            <div className="min-h-screen rounded-2xl sm:rounded-3xl relative bg-[#080808] text-white p-4 sm:p-6 overflow-x-hidden">
                <div className="w-full">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
                        <div>
                            <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                                Prompts & Filters
                            </h1>
                            <p className="text-[#8b8f8d] mt-2 text-sm sm:text-base">
                                Each filter is an original STL (Box), cover image, heading, and optional description. Active filters appear in Smile Design Studio.
                            </p>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-center w-full lg:w-auto">
                            <button
                                onClick={() => setShowCreateModal(true)}
                                className="w-full sm:w-auto px-5 py-3 rounded-xl bg-cyan-600 hover:bg-cyan-700 transition-all font-medium"
                            >
                                + Create Filter
                            </button>
                            <div className="bg-[#101211] border border-[#1f2221] px-5 py-3 sm:py-4 rounded-2xl shadow-xl w-full sm:w-auto">
                                <p className="text-[#8b8f8d] text-sm">Total Filters</p>
                                <h2 className="text-2xl sm:text-3xl font-bold text-cyan-400 mt-1">
                                    {promptsPagination?.totalCount ?? allPrompts.length}
                                </h2>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 sm:items-center mb-6">
                        <div className="relative flex-1">
                            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6b6f6d] text-lg" />
                            <input
                                className="w-full bg-[#101211] border border-[#1f2221] rounded-2xl py-3 pl-12 pr-4 text-white placeholder:text-[#6b6f6d] focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all"
                                placeholder="Search filters..."
                                value={search}
                                onChange={(e) => handleSearchChange(e.target.value)}
                            />
                        </div>
                        <div className="flex bg-[#101211] border border-[#1f2221] rounded-2xl p-1 shrink-0">
                            {([
                                { id: "all", label: "All" },
                                { id: "active", label: "Active" },
                                { id: "inactive", label: "Inactive" },
                            ] as const).map((option) => (
                                <button
                                    key={option.id}
                                    type="button"
                                    onClick={() => handleStatusFilterChange(option.id)}
                                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                                        statusFilter === option.id
                                            ? "bg-cyan-600 text-white"
                                            : "text-[#8b8f8d] hover:text-white"
                                    }`}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {loading && !busy && (
                        <div className="bg-[#101211] border border-[#1f2221] rounded-2xl p-5 text-[#b7bab8] animate-pulse mb-6">
                            Loading filters…
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-500/10 border border-red-500/20 text-red-400 rounded-2xl p-4 mb-6">
                            {error}
                        </div>
                    )}

                    {!loading && !allPrompts.length && (
                        <div className="bg-[#101211] border border-[#1f2221] rounded-2xl p-5 text-[#8b8f8d] mb-6">
                            {statusFilter === "active"
                                ? "No active filters."
                                : statusFilter === "inactive"
                                    ? "No inactive filters."
                                    : "No filters found."}
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {allPrompts.map((p) => (
                            <div
                                key={p.id}
                                className="bg-[#101211] border border-[#1f2221] rounded-3xl p-5 shadow-2xl flex flex-col justify-between"
                            >
                                <PromptDisplay
                                    prompt={p}
                                    isEditing={editId === p.id}
                                    submitting={busy && editId === p.id}
                                    editName={editName}
                                    editHeading={editHeading}
                                    setEditHeading={setEditHeading}
                                    editDescription={editDescription}
                                    editActive={editActive}
                                    editStlFile={editStlFile}
                                    editCoverFile={editCoverFile}
                                    setEditName={setEditName}
                                    setEditDescription={setEditDescription}
                                    setEditActive={setEditActive}
                                    setEditStlFile={setEditStlFile}
                                    setEditCoverFile={setEditCoverFile}
                                    autoResizeTextarea={autoResizeTextarea}
                                    onEdit={() => {
                                        setEditId(p.id);
                                        setEditName(p.name);
                                        setEditHeading(p.heading || p.name || "");
                                        setEditDescription(
                                            typeof p.value === "string" &&
                                                !p.value.trim().startsWith("{")
                                                ? p.value
                                                : ""
                                        );
                                        setEditActive(p.active);
                                        setEditStlFile(null);
                                        setEditCoverFile(null);
                                    }}
                                    onSave={() => handleSaveEdit(p.id)}
                                    onCancel={() => {
                                        setEditId(null);
                                        setEditStlFile(null);
                                        setEditCoverFile(null);
                                    }}
                                    onDelete={() =>
                                        setModal({
                                            type: "delete",
                                            id: p.id,
                                        })
                                    }
                                />
                            </div>
                        ))}
                    </div>

                    <TablePagination
                        pagination={promptsPagination}
                        onPageChange={(nextPage) => {
                            setPage(nextPage);
                            loadPrompts({ page: nextPage });
                        }}
                        itemLabel="filters"
                    />

                    {modal.type && (
                        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                            <div className="w-full max-w-md bg-[#101211] border border-[#1f2221] rounded-3xl p-6 shadow-2xl">
                                <h2 className="text-2xl font-bold mb-3 text-white">
                                    Confirm Delete
                                </h2>
                                <p className="text-[#8b8f8d] mb-6">
                                    Are you sure you want to delete this filter?
                                </p>
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => setModal({ type: null })}
                                        className="flex-1 bg-[#1f2221] hover:bg-[#2a2d2c] transition-all py-3 rounded-xl"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={confirmAction}
                                        className="flex-1 py-3 rounded-xl transition-all bg-red-600 hover:bg-red-700"
                                    >
                                        Confirm
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    {showCreateModal && (
                        <CreatePromptModal
                            name={name}
                            heading={heading}
                            description={description}
                            active={active}
                            stlFile={stlFile}
                            coverFile={coverFile}
                            submitting={busy}
                            setName={setName}
                            setHeading={setHeading}
                            setDescription={setDescription}
                            setActive={setActive}
                            setStlFile={setStlFile}
                            setCoverFile={setCoverFile}
                            autoResizeTextarea={autoResizeTextarea}
                            onSubmit={handleCreatePrompt}
                            onClose={() => {
                                if (!busy) setShowCreateModal(false);
                            }}
                        />
                    )}
                </div>
            </div>
        </>
    );
}
